import { useState } from "react";
import { CommunityPost } from "@/mocks/community";

interface Props {
  post: CommunityPost;
}

export default function PostCard({ post }: Props) {
  const [liked, setLiked] = useState(false);
  const [helped, setHelped] = useState(false);
  const [likes, setLikes] = useState(post.likes);
  const [helpers, setHelpers] = useState(post.helpers);
  const [showReply, setShowReply] = useState(false);
  const [reply, setReply] = useState("");
  const [replies, setReplies] = useState<string[]>([]);

  const handleLike = () => {
    setLiked(l => !l);
    setLikes(n => liked ? n - 1 : n + 1);
  };

  const handleHelp = () => {
    setHelped(h => !h);
    setHelpers(n => helped ? n - 1 : n + 1);
  };

  const handleReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setReplies(r => [...r, reply.trim()]);
    setReply("");
    setShowReply(false);
  };

  return (
    <div className="bg-white rounded-2xl p-5 hover:shadow-sm transition-shadow">
      {/* Author */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-full overflow-hidden shrink-0">
          <img src={post.avatar} alt={post.author} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-gray-900 text-sm">{post.author}</span>
            <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${post.roleColor}`}>
              <i className={post.roleIcon}></i>{post.role}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
            <span>{post.time}</span>
            <span>·</span>
            <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{post.category}</span>
          </div>
        </div>
        <div className="shrink-0 text-xl" title={post.emotion}>{post.emotionEmoji}</div>
      </div>

      {/* Content */}
      <p className="text-gray-700 text-sm leading-relaxed mb-4">{post.content}</p>

      {/* Emotion tag */}
      <div className="flex items-center gap-1.5 mb-4">
        <span className="text-xs text-gray-400">Feeling:</span>
        <span className="bg-rose-50 text-rose-500 text-xs font-semibold px-2.5 py-0.5 rounded-full">{post.emotionEmoji} {post.emotion}</span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
        <button
          onClick={handleLike}
          className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${liked ? "bg-rose-100 text-rose-500" : "bg-gray-50 text-gray-500 hover:bg-rose-50 hover:text-rose-400"}`}
        >
          <i className={liked ? "ri-heart-fill" : "ri-heart-line"}></i> {likes}
        </button>

        <button
          onClick={() => setShowReply(r => !r)}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-gray-50 text-gray-500 hover:bg-blue-50 hover:text-blue-500 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-chat-3-line"></i> {post.replies + replies.length}
        </button>

        {post.helpers > 0 && (
          <button
            onClick={handleHelp}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${helped ? "bg-green-100 text-green-600" : "bg-gray-50 text-gray-500 hover:bg-green-50 hover:text-green-500"}`}
          >
            <i className={helped ? "ri-hand-heart-fill" : "ri-hand-heart-line"}></i> {helpers} helped
          </button>
        )}

        <button className="ml-auto flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-50 text-amber-500 hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-share-forward-line"></i> Share
        </button>
      </div>

      {/* Reply box */}
      {showReply && (
        <form onSubmit={handleReply} className="mt-3 flex gap-2">
          <input
            value={reply}
            onChange={e => setReply(e.target.value)}
            placeholder="Write a supportive reply..."
            className="flex-1 border border-gray-200 rounded-full px-4 py-2 text-sm outline-none focus:border-rose-300 transition-colors"
          />
          <button type="submit" className="bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap">Send</button>
        </form>
      )}

      {/* User replies */}
      {replies.length > 0 && (
        <div className="mt-3 flex flex-col gap-2">
          {replies.map((r, i) => (
            <div key={i} className="bg-gray-50 rounded-xl px-4 py-2.5 text-sm text-gray-700">
              <span className="font-semibold text-gray-500 text-xs">You · just now</span>
              <p className="mt-0.5">{r}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
